import { useState, useEffect, FunctionComponent } from 'react';
import { FormControlLabel } from '../../tailwindcomponents';
import { Button, Checkbox } from '../../tailwindcomponents';
import { DatePicker } from './../../MaterialUiComponents';
import { Typography, Select, SelectMultiple, Table, TableRow, TableCell } from './../../tailwindcomponents';
import { Certificates, CertificateData, SelectObject } from './../../types/certificates';
import { CheckCircle, RemoveCircle, Info } from '../../components/icons';
import Certificate from './Certificate';
import CertificatesApi from '../../api/certificates';
import { EmployeeSearch, PDFCreate, ProfileImg } from '../../components';
import { useSelector } from 'react-redux'
import { RootState } from '../../reducer';

type CertificateProps = {
    setLoading: Function,
    loading: boolean
}

const History: FunctionComponent<CertificateProps> = ({ setLoading, loading }) => {
    const [selectedEmployeeId, setSelectedEmployeeId] = useState<number>(0);
    const [selectedCertificate, setSelectedCertificate] = useState<CertificateData>();
    const [selectedType, setSelectedType] = useState<string>("All Certificates");
    const [orderByText, setOrderByText] = useState<string>("Issue Date");
    const [ascending, setAscending] = useState<boolean>(false);
    const [selectedDateFrom, setSelectedDateFrom] = useState<Date | null>(null);
    const [selectedDateTo, setSelectedDateTo] = useState(new Date());
    const [certificatesResult, setCertificatesResult] = useState(Array<Certificates>());
    const [ratingOptions, setRatingOptions] = useState<SelectObject[]>([]);
    const [selectedRatingOptions, setSelectedRatingOptions] = useState<number[]>([]);
    const [aircraftOptions, setAircraftOptions] = useState<SelectObject[]>([]);
    const [selectedAircraftOptions, setSelectedAircraftOptions] = useState<number[]>([]);
    const [onlyValid, setOnlyValid] = useState<boolean>(false);
    const [clearEmployee, setClearEmployee] = useState<boolean>(false);
    const [selectAll, setSelectAll] = useState<boolean>(false);
    const users = useSelector((state: RootState) => state.employees.all_employees);

    // Gets rating and aircrafts for dropdown upon first render when ratings are empty
    // Refreshes the search on every field change.
    useEffect(() => {
        if (ratingOptions.length === 0) {
            getRatingsAndAircrafts();
        }
        setClearEmployee(false);
        refresh(ascending, orderByText);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ascending, orderByText, selectedDateFrom, selectedDateTo, selectedRatingOptions, selectedAircraftOptions, onlyValid, selectedEmployeeId, clearEmployee, selectedType])

    const onSelectedEmployeeRow = (employee_id: number) => {
        setSelectedEmployeeId(employee_id);
    }
    // Refreshes the search. Run on every search criteria change through useEffect
    const refresh = (ascending: Boolean, orderByText: string) => {
        setLoading(true);
        CertificatesApi.GetCertificateHistoricalData(selectedType, orderByText, ascending, selectedAircraftOptions, selectedRatingOptions, onlyValid, selectedEmployeeId, selectedDateFrom, selectedDateTo).then(res => {
            setCertificatesResult(res.data);
            setLoading(false);
        })
    }
    const onRowClick = (row: Certificates) => {
        CertificatesApi.GetCertificateById(row.certificate_id).then(res => {
            setSelectedCertificate({
                ...res.data,
                certificate_expired_date_text: row.certificate_expired_date_text,
                expired_date_status: row.expired_date_status,
            });
        })
    }
    const onTypeChange = (type: any) => {
        setSelectedType(type);
    }
    const generateTableBody = (row: Certificates, index: number) => {
        return (
            <TableRow onClick={() => onRowClick(row)} >
                <TableCell>{index}</TableCell>
                <TableCell wrap={false}>
                    <div className="flex items-center gap-2">
                        <ProfileImg imgKey={index} imgSize="xs" userName={users?.find(s => s.Id === row.mechanic_employee_id)?.userName?.split('@')[0]} />
                        <Typography align="left" display="inline" text={row.mechanic_full_name}></Typography>
                    </div>
                </TableCell>
                <TableCell>{row.quality_assurance_accepted_date_text}</TableCell>
                <TableCell>{row.certificate_expired_date_text} </TableCell>
                <TableCell>
                    {
                        row.invalid_date !== null && row.quality_assurance_accepted_date === null ? "Declined" :
                            row.invalid_date !== null && row.quality_assurance_accepted_date !== null ? "Inactive" :
                                row.expired_date_status === 'Valid' ? <CheckCircle className="text-green-600" />
                                    :
                                    row.expired_date_status === 'Expiring' ? <Info color="secondary" />
                                        :
                                        <RemoveCircle color="error" />
                    }
                </TableCell>
                <TableCell>
                    {row.quality_assurance_accepted_date === null && row.declined_reason}
                </TableCell>
            </TableRow>
        )
    }
    const OnOrderByTitles = (givenOrderByText: string) => {
        if (orderByText === givenOrderByText) {
            setAscending(!ascending);
        }
        setOrderByText(givenOrderByText);
    }
    const closeCertificate = () => {
        setSelectedCertificate(undefined);
    }
    // Change function for both date fields
    const onDateChange = async (date: Date, type: 'from' | 'to') => {
        if (type === 'from') {
            setSelectedDateFrom(date);
        }
        if (type === 'to') {
            setSelectedDateTo(date);
        }
    }
    // Change function for both Aircrafts and Ratings dropdown
    const onSelectedRating = (selectedRating: number[], type: string) => {
        if (type === "rating") {
            if (selectedRating.includes(999) && selectAll === false) {
                setSelectAll(!selectAll);
                setSelectedRatingOptions([]);
            }
            else if (selectedRating.includes(999)) {
                const options: number[] = [];
                ratingOptions.forEach(rating => {
                    if (rating.id !== 999) {
                        options.push(rating.id);
                    }
                });
                setSelectedRatingOptions(options);
                setSelectAll(!selectAll);
            }
            else {
                setSelectedRatingOptions(selectedRating);
            }
        }
        if (type === "aircraft") {
            setSelectedAircraftOptions(selectedRating);
        }
    }
    // Function to populate dropdown for Rating and Aircrafts. Run through useEffect when ratings hasn't been populated
    const getRatingsAndAircrafts = () => {
        let tempRating: Array<SelectObject> = [];
        let tempAircrafts: Array<SelectObject> = [];
        setLoading(true);
        CertificatesApi.GetAllCertificationRatings().then(res => {
            res.data.filter(r => r.visible === true).map(rating => (
                tempRating.push({ name: rating.rating, id: rating.id })
            ));
            CertificatesApi.GetAllCertififcateAircraftCategories().then(res => {
                res.data.filter(a => a.visible === true).map(aircraft => (
                    tempAircrafts.push({ name: aircraft.aircraft_category, id: aircraft.id })
                ))
                setLoading(false);
                const selectAllObj = { name: "Select all", id: 999 } as SelectObject;
                tempRating.unshift(selectAllObj);
                setRatingOptions(tempRating);
                setAircraftOptions(tempAircrafts);
            })
        })
    }
    // onClick function for resetting all search fields
    const onResetFields = () => {
        setSelectedDateFrom(null);
        setSelectedDateTo(new Date());
        setSelectedRatingOptions([]);
        setSelectedAircraftOptions([]);
        setOnlyValid(false);
        setSelectedEmployeeId(0);
        setClearEmployee(true);
    }

    return (
        <div>
            {selectedCertificate === undefined ?
                <div>
                    <div className="ds-filter-bar">
                        <EmployeeSearch reset={clearEmployee} place_holder={"All employees"} showDisabledEmployees onSelectedEmployee={(employee_id: number) => onSelectedEmployeeRow(employee_id)} />
                        <DatePicker onDateChange={(date: Date) => onDateChange(date, 'from')} type={"Date"} label="Issued from" selected={selectedDateFrom} />
                        <DatePicker onDateChange={(date: Date) => onDateChange(date, 'to')} type={"Date"} label="Issued to" selected={selectedDateTo} />
                        <SelectMultiple optionsObject={ratingOptions} selectedItems={selectedRatingOptions} width={200} onSelectedItem={(e: any) => onSelectedRating(e.target.value, "rating")} filter_by_text="Ratings" />
                        <SelectMultiple optionsObject={aircraftOptions} selectedItems={selectedAircraftOptions} width={200} onSelectedItem={(e: any) => onSelectedRating(e.target.value, "aircraft")} filter_by_text="Aircrafts" />
                        <Select onSelectedItem={(e: any) => onTypeChange(e.target.value)} selectedItem={selectedType} width={200} filter_by_text={"Certificate Type"} options={["All Certificates", "Icelandair Employees", "Subcontracts"]} />
                    </div>
                    <div className="ds-filter-summary">
                        <div className="ds-filter-actions">
                            <FormControlLabel
                                control={<Checkbox color="primary" checked={onlyValid} onChange={() => { setOnlyValid(!onlyValid) }} />}
                                label="Valid Certificates only"
                            />
                            <Button variant="text" color="primary" size="small" onClick={() => onResetFields()}>Reset filters</Button>
                        </div>
                        <Typography text={certificatesResult !== undefined ? certificatesResult.length + " certificates found" : '0 certificates found'} />
                    </div>
                    <div className="flex flex-wrap gap-2 mt-2 mb-2">
                        <div>
                            <PDFCreate
                                setLoading={setLoading}
                                loading={loading}
                                buttonText="Export certificates"
                                selectedType={selectedType}
                                orderByText={orderByText}
                                ascending={ascending}
                                selectedAircraftOptions={selectedAircraftOptions}
                                selectedRatingOptions={selectedRatingOptions}
                                onlyValid={onlyValid}
                                selectedEmployeeId={selectedEmployeeId}
                                selectedDateFrom={selectedDateFrom}
                                selectedDateTo={selectedDateTo}
                                isOnlySelectedFields={false}
                            />
                        </div>
                        <div>
                            <PDFCreate
                                setLoading={setLoading}
                                loading={loading}
                                buttonText="Export selected Ratings/Aircrafts"
                                selectedType={selectedType}
                                orderByText={orderByText}
                                ascending={ascending}
                                selectedAircraftOptions={selectedAircraftOptions}
                                selectedRatingOptions={selectedRatingOptions}
                                onlyValid={onlyValid}
                                selectedEmployeeId={selectedEmployeeId}
                                selectedDateFrom={selectedDateFrom}
                                selectedDateTo={selectedDateTo}
                                isOnlySelectedFields={true}
                            >
                            </PDFCreate>
                        </div>
                    </div>
                    <div>
                        <Table
                            headers={["Employee Name", "Issue Date", "Expiration Date", "Status", "Declined Reason"]}
                            rows={certificatesResult}
                            generateBody={(row: any, index: number) => generateTableBody(row, index)}
                            defaultRowsPerPage={25}
                            OnOrderByTitles={(order_by_text: string) => OnOrderByTitles(order_by_text)}
                        />
                    </div>
                </div>
                :
                <div>
                    <Certificate
                        setLoading={(loadingStatus: boolean) => setLoading(loadingStatus)}
                        selectedCertificate={selectedCertificate}
                        setSelectedCertificate={setSelectedCertificate}
                        closeCertificate={() => closeCertificate()}
                        viewType='history'
                        userType={"CertAdmin"}
                    />
                </div>
            }
        </div>
    );
};
export default History;
